import { FC, useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Accept, useDropzone } from 'react-dropzone';

import { readAsDataURL } from '../../../utils/imageUtils';
import { ImageType } from './types';
import UploadIcon from '../../../assets/images/svg/upload.svg';
import Image from 'next/image';
import { baseStyle, focusedStyle, acceptStyle, rejectStyle } from './styles';

type Props = {
  getFiles: (files: ImageType[]) => void;
  validFileExtensions?: Accept;
  minHeight?: string;
  minWidth?: string;
  title?: string;
};

export const UploadFile: FC<Props> = ({
  getFiles,
  validFileExtensions = { 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'], 'image/svg+xml': ['.svg'] },
  minHeight,
  minWidth,
  title,
}) => {
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      let images: any[] = await Promise.all(
        acceptedFiles.map((f) => {
          return readAsDataURL(f);
        })
      );
      return getFiles(images || []);
    },
    [getFiles]
  );
  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject, fileRejections } = useDropzone({
    onDrop,
    accept: validFileExtensions,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      minHeight: minHeight ? minHeight : '100px',
      minWidth: minWidth ? minWidth : '100px',
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
      ...(isFocused && !fileRejections?.length ? acceptStyle : {}),
      ...(isFocused && fileRejections?.length ? rejectStyle : {}),
    }),
    [minHeight, minWidth, isFocused, isDragAccept, isDragReject, fileRejections?.length]
  );

  return (
    <div {...getRootProps({ style })}>
      <input {...getInputProps()} />
      <Box display='flex' alignItems='center'>
        <Image src={UploadIcon} alt='Upload' />
        <Typography variant='h4' fontSize='18px' ml={1} color='#535353'>
          {title ?? 'Upload file'}
        </Typography>
      </Box>
    </div>
  );
};
